import { createClient } from '@supabase/supabase-js'
import type { Database, Json } from '@/types/supabase'
import type { MachineModel, SupportMessage, FollowUpAnswer, CommonProblem, RareProblem } from '@/types'
import { storage } from './utils'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Supabase environment variables missing. Check .env.local')
}

// ── Session Token (browser_token) ─────────────────────────────────────────
// Jede qr_support_sessions-Zeile hat eine zufällige browser_token UUID.
// Der Client speichert sie in sessionStorage und sendet sie als x-session-token Header.
// PostgREST-RLS liest den Header via current_setting('request.headers') → nur eigene Session
// sichtbar. Realtime-WebSocket kann keinen Custom-Header senden → neue Nachrichten kommen per
// Broadcast auf support:<session_id>:<sha256(browser_token)> (Trigger aus Migration 0029).

let _sessionToken: string | null = null

export function setSessionToken(token: string): void {
  _sessionToken = token
  storage.set('vendi_session_token', token)
}

export function restoreSessionToken(): string | null {
  if (_sessionToken) return _sessionToken
  const saved = storage.get('vendi_session_token')
  if (saved) _sessionToken = saved
  return _sessionToken
}

// Typen aus der Live-Datenbank (npm run gen:types, V0-04): Schemaänderung → Build bricht hier
export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  // Herzschlag-Probleme des Echtzeit-Kanals in die Konsole (OPS-Testing zeigt sie im Live-Protokoll)
  realtime: {
    heartbeatCallback: (status) => {
      if (status === 'timeout' || status === 'disconnected') realtimeLog(`heartbeat ${status}`)
    },
  },
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    detectSessionInUrl: false,
  },
  global: {
    fetch: (url, options = {}) => {
      if (!_sessionToken) return fetch(url, options as RequestInit)
      const headers = new Headers((options as RequestInit).headers)
      headers.set('x-session-token', _sessionToken)
      return fetch(url, { ...(options as RequestInit), headers })
    },
  },
})

// ─── Fehler-Klassifizierung für den Router-Loader ─────────────────────────────

/** Wird vom Loader geworfen, wenn Supabase nicht erreichbar ist (≠ „nicht gefunden"). */
export class ConnectionError extends Error {
  constructor(message = 'connection_failed') {
    super(message)
    this.name = 'ConnectionError'
  }
}

// Postgres-Fehlercodes, die „kein Treffer" bedeuten und KEIN Verbindungsproblem sind.
const NOT_FOUND_CODES = new Set([
  'PGRST116', // .single(): 0 rows
  '22P02',    // invalid input syntax for type uuid (Legacy model_id statt UUID)
])

function isNotFound(error: { code?: string } | null): boolean {
  return !!error && !!error.code && NOT_FOUND_CODES.has(error.code)
}

// ─── Normalisierung der JSON-Spalten (defensiv gegen unvollständige Daten) ────

function normalizeProblems(raw: unknown): CommonProblem[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .filter((p) => typeof p.id === 'string' && typeof p.title === 'string' && p.title.trim() !== '')
    .map((p) => ({
      id: p.id as string,
      title: (p.title as string).trim(),
      description: typeof p.description === 'string' ? p.description : '',
      icon: typeof p.icon === 'string' ? p.icon : 'default',
      solution: typeof p.solution === 'string' && p.solution.trim() ? p.solution : null,
      follow_ups: Array.isArray(p.follow_ups)
        ? (p.follow_ups as Record<string, unknown>[])
            .filter((f) => f && typeof f.question === 'string' && Array.isArray(f.options))
            .map((f) => ({
              question: f.question as string,
              options: (f.options as Record<string, unknown>[])
                .filter((o) => o && typeof o.label === 'string')
                .map((o) => ({
                  label: o.label as string,
                  solution: typeof o.solution === 'string' ? o.solution : '',
                })),
            }))
            .filter((f) => f.options.length > 0)
        : [],
    }))
}

function normalizeRareProblems(raw: unknown): RareProblem[] {
  if (!Array.isArray(raw)) return []
  return raw
    .filter((p): p is Record<string, unknown> => !!p && typeof p === 'object')
    .filter((p) => typeof p.id === 'string' && typeof p.title === 'string')
    .map((p) => ({ id: p.id as string, title: p.title as string }))
}

// ─── Machine Models ───────────────────────────────────────────────────────────

export async function getMachineModel(modelId: string): Promise<MachineModel | null> {
  const { data, error } = await supabase
    .from('machine_models')
    // ohne support_phone (V2-10): die Nummer kommt nur mit Betreiberfreigabe und nach Tap über ai-support
    .select('id, model_id, display_name, description, image_url, color, location_hint, common_problems, rare_problems, is_active, created_at')
    .eq('model_id', modelId)
    .eq('is_active', true)
    .maybeSingle()

  if (error) {
    if (isNotFound(error)) return null
    throw new ConnectionError(error.message)
  }
  if (!data) return null
  return {
    ...(data as unknown as MachineModel),
    common_problems: normalizeProblems(data.common_problems),
    rare_problems: normalizeRareProblems(data.rare_problems),
  }
}

// ─── Device lookup (QR-Code flow) ─────────────────────────────────────────────
// Maps a physical machine (from locations table) to MachineModel shape.
// Joins onto machine_models so the ClickFlow has common_problems/rare_problems
// available without an extra round trip.

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

export async function getDeviceForSupport(deviceId: string): Promise<MachineModel | null> {
  // Legacy-IDs (machine_models.model_id, z.B. "schmidts-grillautomat") sind keine UUIDs →
  // Lookup überspringen statt einen 400 (invalid uuid) zu provozieren.
  if (!UUID_RE.test(deviceId)) return null
  // Eigene RPC statt direktem Select auf `locations` (seit 27.10.2026,
  // Migration 0060): die öffentliche Leseregel `locations_public_read` blendet
  // Zigarettenautomaten aus, damit sie nicht auf der Karte der VENDi App
  // stehen. Der QR-Support muss sie trotzdem finden. Die RPC prüft dieselben
  // Bedingungen wie bisher (veröffentlicht, aktiv, Laufzeit nicht abgelaufen
  // — Server-Zeit, auf die Sekunde) und liefert dieselben Felder.
  const { data: raw, error } = await supabase.rpc('get_device_for_support', {
    p_device_id: deviceId,
  })

  if (error) {
    if (isNotFound(error)) return null
    throw new ConnectionError(error.message)
  }
  if (!raw) return null
  const data = raw as {
    id: string
    name: string | null
    address: string | null
    model: string | null
    manufacturer: string | null
    created_at: string
    machine_model_id: string | null
    machine_model: unknown
  }

  const mm = (data.machine_model ?? null) as Partial<MachineModel> | null

  const displayName =
    mm?.display_name || data.name || data.model || 'Automat'
  const description =
    mm?.description || [data.manufacturer, data.model].filter(Boolean).join(' ') || null

  return {
    id: data.id,
    // Always carry the location's UUID — ai-support edge fn expects device_id here
    model_id: data.id,
    display_name: displayName,
    description,
    image_url: mm?.image_url ?? null,
    color: mm?.color ?? null,
    location_hint: mm?.location_hint ?? data.address ?? null,
    common_problems: normalizeProblems(mm?.common_problems),
    rare_problems: normalizeRareProblems(mm?.rare_problems),
    is_active: true,
    created_at: data.created_at,
  } as MachineModel
}

// ─── Support Sessions ─────────────────────────────────────────────────────────

export async function createSupportSession(params: {
  deviceId: string
  abVariant: 'chat' | 'click'
  source?: 'web_qr' | 'mobile' | 'kiosk'
  language?: 'de' | 'en' | 'fr' | 'it'
}): Promise<{ id: string; browser_token: string } | null> {
  // Token client-seitig generieren und VOR dem Insert in den Custom-Header schreiben.
  // Sonst greift die SELECT-RLS-Policy (`browser_token = header`) nicht beim
  // RETURNING der Insert-Anfrage → data wäre null.
  const browserToken = crypto.randomUUID()
  setSessionToken(browserToken)

  const { data, error } = await supabase
    .from('qr_support_sessions')
    .insert({
      device_id: params.deviceId,
      source: params.source ?? 'web_qr',
      language: params.language ?? 'de',
      status: 'ai_active',
      ai_mode: true,
      ab_variant: params.abVariant,
      browser_token: browserToken,
    })
    .select('id, browser_token')
    .single()

  if (error) {
    console.error('[createSupportSession]', error)
    lastSessionError = sessionErrorKind(error)
    return null
  }
  lastSessionError = null
  return data
}

/**
 * Warum eine Session nicht angelegt werden konnte (DESIGN_UX.md Teil 4, Befund 8): Netz vs. Automat unbekannt.
 * 23503 = Fremdschlüssel (Gerät steht nicht in locations), Netzfehler haben keinen Code.
 */
export type SessionErrorKind = 'network' | 'unknown_device' | 'other'
let lastSessionError: SessionErrorKind | null = null
export function getLastSessionError(): SessionErrorKind | null {
  return lastSessionError
}
export function sessionErrorKind(error: { code?: string | null; message?: string | null }): SessionErrorKind {
  if (error.code === '23503' || error.code === '22P02') return 'unknown_device'
  if (!error.code || /fetch|network|timeout|load failed/i.test(error.message ?? '')) return 'network'
  return 'other'
}

/** Startkarten-Reihenfolge nach Häufigkeit (Befund 9). Vor Migration 0040 oder bei Fehlern: null. */
export async function fetchStartCardOrder(locationId: string): Promise<{ problem_key: string; n: number }[] | null> {
  if (!/^[0-9a-f-]{36}$/i.test(locationId)) return null
  const { data, error } = await supabase.rpc('support_start_card_order', { p_location: locationId })
  if (error) return null
  return (data ?? []) as { problem_key: string; n: number }[]
}

// ── Chat-Session über Reload hinweg merken (nur Chat-Variante) ───────────────
// Nach einem versehentlichen Reload/Tab-Wechsel bleibt das Gespräch erhalten.

interface StoredChatSession {
  id: string
  token: string
}

function chatSessionKey(deviceId: string) {
  return `vendi_chat_${deviceId}`
}

export function saveChatSession(deviceId: string, session: StoredChatSession): void {
  storage.set(chatSessionKey(deviceId), JSON.stringify(session))
}

export function loadChatSession(deviceId: string): StoredChatSession | null {
  const raw = storage.get(chatSessionKey(deviceId))
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw)
    if (typeof parsed?.id === 'string' && typeof parsed?.token === 'string') return parsed
  } catch {
    /* fall through */
  }
  return null
}

// ── „Fall merken“ (V1-10): Link mit Session und Token im #-Teil (geht nie an einen Server) ──
const UUID_PART = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'

/** Link, mit dem der Kunde den Fall später auf einem beliebigen Handy wieder öffnet. */
export function caseResumeLink(deviceId: string): string | null {
  const s = loadChatSession(deviceId)
  if (!s) return null
  return `${window.location.origin}/support/${encodeURIComponent(deviceId)}#fall=${s.id}.${s.token}`
}

/** Öffnet ein gemerkter Link die Seite, wird die Session übernommen und der #-Teil sofort entfernt. */
export function adoptResumeLink(deviceId: string): boolean {
  const m = window.location.hash.match(new RegExp(`^#fall=(${UUID_PART})\\.(${UUID_PART})$`, 'i'))
  if (!m) return false
  saveChatSession(deviceId, { id: m[1], token: m[2] })
  window.history.replaceState(null, '', window.location.pathname + window.location.search)
  return true
}

export function clearChatSession(deviceId: string): void {
  storage.remove(chatSessionKey(deviceId))
}

/** Liefert Status + bisherige Nachrichten einer Session (RLS via x-session-token). */
export async function fetchSessionHistory(sessionId: string): Promise<{
  status: string
  turnstileVerified: boolean
  messages: SupportMessage[]
} | null> {
  const { data: session, error: sErr } = await supabase
    .from('qr_support_sessions')
    .select('id, status, turnstile_verified_at')
    .eq('id', sessionId)
    .maybeSingle()
  if (sErr || !session) return null

  const { data: messages, error: mErr } = await supabase
    .from('qr_support_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
  if (mErr) return null

  return {
    status: session.status as string,
    turnstileVerified: !!session.turnstile_verified_at,
    messages: (messages ?? []) as SupportMessage[],
  }
}

/** Nachrichten, die nach einem Zeitpunkt eingetroffen sind (Polling-Fallback). `null` = nicht erreichbar. */
export async function fetchMessagesSince(
  sessionId: string,
  sinceIso: string | null,
): Promise<SupportMessage[] | null> {
  let query = supabase
    .from('qr_support_messages')
    .select('*')
    .eq('session_id', sessionId)
    .order('created_at', { ascending: true })
  if (sinceIso) query = query.gt('created_at', sinceIso)
  try {
    const { data, error } = await query
    if (error) return null
    return (data ?? []) as SupportMessage[]
  } catch {
    return null
  }
}

export async function sendSupportMessage(params: {
  sessionId: string
  role: 'user' | 'ai' | 'operator' | 'system'
  content: string
  meta?: Record<string, unknown>
  /** V1-11: feste ID je Nachricht; Wiederholungen mit derselben ID ergeben keine zweite Zeile */
  clientMsgId?: string
}): Promise<{ id: string; created_at: string } | null> {
  const row: Database['public']['Tables']['qr_support_messages']['Insert'] = {
    session_id: params.sessionId,
    role: params.role,
    content: params.content,
    meta: (params.meta ?? null) as Json,
  }
  if (params.clientMsgId) row.client_msg_id = params.clientMsgId
  const { data, error } = await supabase
    .from('qr_support_messages')
    .insert(row)
    .select('id, created_at')
    .single()

  if (error) {
    // Schon angekommen (Doppeltap / Wiederholung nach Netzabbruch): vorhandene Zeile zurückgeben
    if (error.code === '23505' && params.clientMsgId) {
      const { data: existing } = await supabase
        .from('qr_support_messages')
        .select('id, created_at')
        .eq('session_id', params.sessionId)
        .eq('client_msg_id', params.clientMsgId)
        .maybeSingle()
      if (existing) return existing
    }
    // Spalte fehlt noch (Migration 0039 nicht angewendet): einmal ohne ID senden
    if (params.clientMsgId && /client_msg_id/.test(error.message ?? '')) {
      return sendSupportMessage({ ...params, clientMsgId: undefined })
    }
    console.error('[sendSupportMessage]', error)
    return null
  }
  return data
}

export type RealtimeStatus = 'connecting' | 'connected' | 'error'

async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Zustand des Echtzeit-Kanals als eine Konsolenzeile mit festem Präfix. VENDi OPS (Testing im
 * eingebetteten Browser) liest genau diese Zeilen mit; im Safari-Web-Inspector stehen sie ebenso.
 */
export function realtimeLog(text: string): void {
  const page = typeof document === 'undefined' ? '' : ` (Seite ${document.visibilityState})`
  console.warn(`[vendi-realtime] ${text}${page}`)
}

/** Wartezeit bis zum nächsten eigenen Verbindungsversuch: 2 s, 5 s, 10 s, dann alle 30 s. */
export function resubscribeDelayMs(attempt: number): number {
  return [2_000, 5_000, 10_000][attempt] ?? 30_000
}

/**
 * Neue Nachrichten der eigenen Session live empfangen (P0-03).
 * Kanalname enthält den Hash des browser_token — nur dieser Browser kennt ihn, fremde
 * Sessions sind nicht abonnierbar. Gesendet wird vom DB-Trigger `broadcast_support_message`.
 *
 * Fällt der Kanal aus, baut diese Funktion ihn selbst neu auf (nach 2/5/10/30 s, sofort beim
 * Zurückkehren auf die Seite oder ins Netz). Die Bibliothek allein verbindet nicht neu, solange der
 * Browser die Seite als verborgen meldet — eingebettete Browser (WebView) tun das teils dauerhaft.
 */
export function subscribeToSupportMessages(
  sessionId: string,
  onMessage: (msg: SupportMessage) => void,
  onStatus?: (status: RealtimeStatus) => void,
): { unsubscribe: () => void } {
  let channel: ReturnType<typeof supabase.channel> | null = null
  let closed = false
  let connected = false
  let attempt = 0
  let retryTimer: number | null = null
  // Zähler je Verbindungsversuch: Laufen zwei Versuche gleichzeitig (Netz zurück + Seite sichtbar), darf nur der
  // neueste einen Kanal anlegen — sonst bliebe der ältere offen (Review vor 4.1.1, 20.10.2026)
  let generation = 0
  const token = restoreSessionToken()
  if (!token) {
    realtimeLog('kein browser_token')
    onStatus?.('error')
    return { unsubscribe: () => {} }
  }

  const drop = () => {
    const old = channel
    channel = null
    if (old) void supabase.removeChannel(old)
  }

  const scheduleRetry = () => {
    if (closed || retryTimer !== null) return
    retryTimer = window.setTimeout(() => {
      retryTimer = null
      attempt += 1
      void open()
    }, resubscribeDelayMs(attempt))
  }

  const open = async () => {
    if (closed) return
    const mineGen = ++generation
    drop()
    let hash: string
    try {
      hash = await sha256Hex(token)
    } catch (e) {
      realtimeLog(`sha256 nicht verfügbar: ${e instanceof Error ? e.message : String(e)}`)
      onStatus?.('error')
      return
    }
    if (closed || mineGen !== generation) return
    const mine = supabase
      .channel(`support:${sessionId}:${hash}`, { config: { private: true } })
      .on('broadcast', { event: 'message' }, ({ payload }) => {
        const msg = payload as SupportMessage
        if (msg?.session_id === sessionId) onMessage(msg)
      })
    channel = mine
    mine.subscribe((status, err) => {
      // Meldungen eines schon ersetzten Kanals (removeChannel meldet CLOSED) nicht weitergeben
      if (closed || channel !== mine) return
      if (status === 'SUBSCRIBED') {
        if (attempt > 0) realtimeLog(`wieder verbunden nach ${attempt} Versuch(en)`)
        connected = true
        attempt = 0
        onStatus?.('connected')
      } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
        realtimeLog(`${status}${err?.message ? `: ${err.message}` : ''}`)
        connected = false
        onStatus?.('error')
        scheduleRetry()
      }
    })
  }

  // Zurück auf der Seite oder wieder Netz: nicht auf den nächsten Versuch warten
  const now = () => {
    if (closed || connected || (typeof document !== 'undefined' && document.visibilityState === 'hidden')) return
    if (retryTimer !== null) window.clearTimeout(retryTimer)
    retryTimer = null
    void open()
  }
  document.addEventListener('visibilitychange', now)
  window.addEventListener('online', now)

  void open()
  return {
    unsubscribe: () => {
      closed = true
      if (retryTimer !== null) window.clearTimeout(retryTimer)
      document.removeEventListener('visibilitychange', now)
      window.removeEventListener('online', now)
      drop()
    },
  }
}

// ─── Click-Flow event logging (writes to qr_support_messages as role=system) ──
// Operator sees the full click-path in vendi_hub via the same messages stream.

export async function logClickEvent(params: {
  sessionId: string
  step: 'problem_selected' | 'follow_up' | 'free_text' | 'resolved' | 'escalated'
  content: string
  problemId?: string
  followUpAnswers?: FollowUpAnswer[]
}) {
  await sendSupportMessage({
    sessionId: params.sessionId,
    role: 'system',
    content: params.content,
    meta: {
      click_step: params.step,
      problem_id: params.problemId,
      follow_up_path: params.followUpAnswers,
    },
  })
}

/** @returns true, wenn alle drei Schritte erfolgreich waren. */
export async function escalateToOperator(params: {
  sessionId: string
  freeText: string
  problemId?: string
  followUpAnswers?: FollowUpAnswer[]
}): Promise<boolean> {
  // 1) Flip status FIRST so the ai-support DB trigger (which fires on
  //    qr_support_messages INSERT) skips this user message instead of
  //    generating an unwanted AI reply.
  const { error: statusErr } = await supabase
    .from('qr_support_sessions')
    .update({ status: 'waiting_operator', ai_mode: false })
    .eq('id', params.sessionId)
  if (statusErr) {
    console.error('[escalateToOperator] status', statusErr)
    return false
  }
  // 2) System message with the click-path context for the operator
  await logClickEvent({
    sessionId: params.sessionId,
    step: 'escalated',
    content: 'Click-Flow eskaliert an Betreiber',
    problemId: params.problemId,
    followUpAnswers: params.followUpAnswers,
  })
  // 3) The user's free-text as a real user message so the operator sees the content
  const msg = await sendSupportMessage({
    sessionId: params.sessionId,
    role: 'user',
    content: params.freeText,
  })
  return !!msg
}

export async function markResolved(params: {
  sessionId: string
  seconds: number
  problemId?: string
}) {
  await logClickEvent({
    sessionId: params.sessionId,
    step: 'resolved',
    content: 'Kunde hat das Problem als gelöst markiert',
    problemId: params.problemId,
  })
  await supabase
    .from('qr_support_sessions')
    .update({
      status: 'resolved',
      resolved_seconds: params.seconds,
      resolved_at: new Date().toISOString(),
    })
    .eq('id', params.sessionId)
}

/**
 * Sterne-Bewertung (1–5) auf die eigene Session schreiben.
 * Braucht Migration 0010 (Spalten rating/rated_at). Schlägt still fehl, wenn sie fehlt.
 */
export type RateResult =
  | { ok: true }
  | { ok: false; reason: 'error' | 'no_row' | 'invalid_session'; status?: number; code?: string; message?: string; hasToken: boolean; attempts: number }

export async function rateSession(sessionId: string, rating: number): Promise<RateResult> {
  const stars = Math.min(5, Math.max(1, Math.round(rating)))
  const hasToken = !!restoreSessionToken()
  // Keine echte Session (z. B. Demo „demo-session“): nicht zum Server schicken. Ursache des OPS-Bugs 687bfdf4 —
  // PostgREST antwortete 400 / 22P02 „invalid input syntax for type uuid“ (nachgestellt 20.10.2026).
  if (!UUID_RE.test(sessionId)) return { ok: false, reason: 'invalid_session', hasToken, attempts: 0 }
  let last: RateResult = { ok: false, reason: 'error', hasToken, attempts: 0 }
  // Ein zweiter Versuch nur bei Netzwerkfehlern (kein HTTP-Status), z. B. kurz weg beim App-Wechsel
  for (let attempt = 1; attempt <= 2; attempt++) {
    const { data, error, status } = await supabase
      .from('qr_support_sessions')
      .update({ rating: stars, rated_at: new Date().toISOString() })
      .eq('id', sessionId)
      .select('id')
    if (!error && data && data.length > 0) return { ok: true }
    // Ohne Fehler, aber keine Zeile: RLS hat die Session nicht gefunden (Token fehlt oder passt nicht)
    last = error
      ? { ok: false, reason: 'error', status, code: error.code, message: error.message, hasToken, attempts: attempt }
      : { ok: false, reason: 'no_row', status, hasToken, attempts: attempt }
    console.error('[rateSession]', last)
    if (!error || status) break
    await new Promise((r) => setTimeout(r, 800))
  }
  return last
}
